import type {
  FindPaymentLogsInput,
  PaymentLogBillingType,
  PaymentLogStatus,
} from '@travel-planner/shared/schemas';
import type { PaymentLogsSearch } from './search-params';
import {
  paymentLogBillingTypeSchema,
  paymentLogStatusSchema,
} from '@travel-planner/shared/schemas';

export const PAYMENT_LOGS_PAGE_SIZE = 10;

const narrow = <T extends string>(
  values: string[] | undefined,
  isValid: (v: string) => v is T
): T[] | undefined => {
  if (!values || values.length === 0) return undefined;
  const next = values.filter(isValid);
  return next.length > 0 ? next : undefined;
};

const isStatus = (v: string): v is PaymentLogStatus =>
  paymentLogStatusSchema.safeParse(v).success;

const isBillingType = (v: string): v is PaymentLogBillingType =>
  paymentLogBillingTypeSchema.safeParse(v).success;

// Translate the URL-driven PaymentLogsSearch into the backend's
// findPaymentLogsInput shape. Pagination is 1-based on the URL; the backend
// reads `offset` (0-based). Org scoping is enforced server-side — there is
// no `organizationId` to pass through.
export const toSearchInput = (
  search: PaymentLogsSearch,
  pageSize: number = PAYMENT_LOGS_PAGE_SIZE
): FindPaymentLogsInput => {
  const page = search.page && search.page > 0 ? search.page : 1;
  return {
    offset: (page - 1) * pageSize,
    limit: pageSize,
    search: search.search,
    statuses: narrow(search.statuses, isStatus),
    billingTypes: narrow(search.billingTypes, isBillingType),
    customerIds:
      search.customerIds && search.customerIds.length > 0
        ? search.customerIds
        : undefined,
    itineraryIds:
      search.itineraryIds && search.itineraryIds.length > 0
        ? search.itineraryIds
        : undefined,
    // Org-wide by default. The URL only carries `accountId` when the user
    // explicitly opts into the per-account filter from the toolbar.
    accountId: search.accountId,
    startDate: search.startDate,
    endDate: search.endDate,
    createdStartDate: search.createdStartDate,
    createdEndDate: search.createdEndDate,
  };
};
